import type { LuckFactor } from "@/lib/conditions/luck";
import { scoreOutOf100, scoreOutOf100Label } from "@/lib/conditions/score-display";

function deltaColor(delta: number): string {
  if (delta >= 1) return "#059669";
  if (delta >= 0.3) return "#84cc16";
  if (delta >= -0.3) return "#f59e0b";
  if (delta >= -1) return "#f97316";
  return "#dc2626";
}

function svgNumber(value: number): number {
  return Number(value.toFixed(4));
}

export function LuckGauge({ luck }: { luck: LuckFactor }) {
  const cx = 100;
  const cy = 82;
  const r = 62;
  const sw = 11;
  const circumference = 2 * Math.PI * r;
  const arcLength = (270 / 360) * circumference;
  const gapLength = circumference - arcLength;

  const score = Math.max(0, Math.min(10, luck.currentScore));
  const typical = Math.max(0, Math.min(10, luck.typicalScore));
  const fillLength = (score / 10) * arcLength;
  const color = deltaColor(luck.delta);

  // Arc runs clockwise from 135° (lower-left) for 270° to 45° (lower-right)
  // SVG angles: 0°=east, 90°=south, 180°=west, 270°=north
  const typAngle = (135 + (typical / 10) * 270) * (Math.PI / 180);
  const tx = cx + Math.cos(typAngle) * r;
  const ty = cy + Math.sin(typAngle) * r;
  const udx = Math.cos(typAngle);
  const udy = Math.sin(typAngle);
  const typicalMarker = {
    x1: svgNumber(tx - udx * 10),
    y1: svgNumber(ty - udy * 10),
    x2: svgNumber(tx + udx * 10),
    y2: svgNumber(ty + udy * 10),
  };

  const absDelta = Math.abs(luck.delta);
  const sign = luck.delta >= 0 ? "+" : "−";

  return (
    <div className="flex flex-col items-center">
      <svg
        viewBox="0 0 200 155"
        width="200"
        height="155"
        role="img"
        aria-label={`Luck: ${scoreOutOf100(luck.currentScore)} out of 100 versus typical ${scoreOutOf100(luck.typicalScore)} for ${luck.weekLabel}`}
      >
        {/* Background track */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#ebebeb"
          strokeWidth={sw}
          strokeDasharray={`${arcLength} ${gapLength}`}
          strokeLinecap="round"
          transform={`rotate(135 ${cx} ${cy})`}
        />

        {/* Score fill */}
        {fillLength > 1 && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={sw}
            strokeDasharray={`${fillLength} ${circumference - fillLength}`}
            strokeLinecap="round"
            transform={`rotate(135 ${cx} ${cy})`}
          />
        )}

        {/* Typical score marker (radial tick) */}
        <line
          x1={typicalMarker.x1}
          y1={typicalMarker.y1}
          x2={typicalMarker.x2}
          y2={typicalMarker.y2}
          stroke="#9ca3af"
          strokeWidth={2.5}
          strokeLinecap="round"
        />

        {/* Centre score */}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="34"
          fontWeight="700"
          fill="#1B2840"
          fontFamily="'Instrument Serif', serif"
        >
          {scoreOutOf100(luck.currentScore)}
        </text>
        <text
          x={cx}
          y={cy + 19}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="11"
          fill="#9ca3af"
        >
          out of 100
        </text>

        {/* Scale labels */}
        <text
          x={cx - 48}
          y={cy + 52}
          textAnchor="middle"
          fontSize="9"
          fontWeight="600"
          fill="#b0b0b0"
        >
          0
        </text>
        <text
          x={cx + 48}
          y={cy + 52}
          textAnchor="middle"
          fontSize="9"
          fontWeight="600"
          fill="#b0b0b0"
        >
          100
        </text>
      </svg>

      <div className="text-center -mt-1">
        {absDelta >= 0.3 && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide text-white"
            style={{ backgroundColor: color }}
          >
            {sign}
            {scoreOutOf100(absDelta)} vs typical
          </span>
        )}
        <div className={`text-[12px] text-text-muted ${absDelta >= 0.3 ? "mt-1.5" : "mt-0.5"}`}>
          Typical{" "}
          <span className="font-semibold text-charcoal">
            {scoreOutOf100Label(luck.typicalScore)}
          </span>{" "}
          for {luck.weekLabel}
        </div>
      </div>
    </div>
  );
}
